// TeachingPage — simplified Phase 3
// Hub for viewing and managing sessions. Shows journey cards with session actions.

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { useSessions } from '../../hooks/useSessions';
import { getTopicsByJourney } from '../../services/firebase';
import Layout from '../layout/Layout';
import Button from '../ui/Button';
import Card from '../ui/Card';
import EmptyState from '../ui/EmptyState';


const getDaysUntilExam = (examDate: Date | string | undefined): number | null => {
  if (!examDate) return null;
  const exam = examDate instanceof Date ? examDate : new Date(examDate);
  const diff = new Date(exam).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const TeachingPage: React.FC = () => {
  const { t } = useTranslation(['teaching', 'common']);
  const navigate = useNavigate();
  const { journeys, loading: journeysLoading, error: journeysError } = useLearningJourneys();
  const { getUnfinishedSessions } = useSessions();

  const [topicCounts, setTopicCounts] = useState<Record<string, number>>({});

  // Fetch topic counts
  useEffect(() => {
    if (journeys.length === 0) return;
    const fetchCounts = async () => {
      const counts: Record<string, number> = {};
      for (const j of journeys) {
        try {
          const result = await getTopicsByJourney(j.id);
          counts[j.id] = result.success && result.topics ? result.topics.length : 0;
        } catch {
          counts[j.id] = 0;
        }
      }
      setTopicCounts(counts);
    };
    fetchCounts();
  }, [journeys]);

  // Unfinished sessions
  const unfinishedSessions = getUnfinishedSessions();

  const handleContinueSession = (sessionId: string) => {
    navigate(`/session/${sessionId}`);
  };

  return (
    <Layout>
      <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex items-center text-sm text-neutral-medium mb-4" aria-label={t('common:accessibility.breadcrumb')}>
            <Link to="/dashboard" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              {t('common:nav.dashboard')}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-neutral-dark dark:text-white font-medium">{t('title')}</span>
          </nav>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-display font-semibold text-neutral-dark dark:text-white">
              {t('title')}
            </h1>
            {journeys.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/journeys/new')}
              >
                {t('createJourney')}
              </Button>
            )}
          </div>
          <p className="text-neutral-medium mt-2">{t('page.subtitle')}</p>
        </div>

        {/* Main content */}
        {journeysLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : journeysError ? (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-500 text-sm text-center">{journeysError}</p>
          </div>
        ) : journeys.length === 0 ? (
          <EmptyState
            type="journeys"
            title={t('noJourneys.title')}
            description={t('noJourneys.description')}
            actionText={t('noJourneys.action')}
            onAction={() => navigate('/journeys/new')}
            size="md"
          />
        ) : (
          <>
            {/* Journey cards */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-4">
                {t('yourJourneys')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {journeys.map((journey) => {
                  const topicCount = topicCounts[journey.id] ?? 0;
                  const hasTopics = topicCount > 0;
                  const daysUntil = getDaysUntilExam(journey.examDate);
                  const journeyUnfinished = unfinishedSessions.filter(s =>
                    s.learningJourneyId === journey.id
                  );

                  return (
                    <Card key={journey.id} padding="md" hover className="cursor-pointer" onClick={() => navigate(`/journeys/${journey.id}`)}>
                      <h3 className="font-semibold text-neutral-dark dark:text-white truncate mb-2">
                        {journey.title || journey.subject}
                      </h3>

                      {/* Meta info */}
                      <div className="flex items-center gap-3 text-sm text-neutral-medium mb-4">
                        <span>
                          {hasTopics
                            ? t('journeyCard.topicCount', { count: topicCount })
                            : t('journeyCard.noTopics')}
                        </span>
                        {daysUntil !== null && (
                          <span className={daysUntil >= 0 && daysUntil <= 7 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                            {daysUntil >= 0
                              ? t('journeyCard.examIn', { count: daysUntil })
                              : t('journeyCard.examPassed')}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                        {hasTopics ? (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => navigate(`/library/journey/${journey.id}`)}
                            >
                              {t('journeyCard.viewPlan')}
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => navigate(`/library/journey/${journey.id}`)}
                          >
                            {t('noTopics.action')}
                          </Button>
                        )}
                      </div>

                      {/* Unfinished sessions for this journey */}
                      {journeyUnfinished.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                          <p className="text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1">
                            <span>⏸️</span> {t('unfinishedSessions.title')}
                          </p>
                          {journeyUnfinished.slice(0, 2).map((session) => {
                            const form = session.teachingForm || 'dialog';
                            const formEmoji = t(`forms.${form}.emoji`);
                            return (
                              <button
                                key={session.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleContinueSession(session.id!);
                                }}
                                className="w-full flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-left hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                              >
                                <span className="text-sm text-neutral-dark dark:text-white flex items-center gap-2">
                                  <span>{formEmoji}</span>
                                  {session.topicTitle || t('activeSession.unknownTopic')}
                                </span>
                                <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                                  {t('common:session.continue')}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
      </div>
    </Layout>
  );
};

export default TeachingPage;
